import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND } from "lexical";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { handleClearFormatting, handleHeading, handleImage, handleLink, handleList, handleParagraph } from "components/ContentEditor/editorHandlers";

export function Toolbar() {
	const [editor] = useLexicalComposerContext();
	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);

	const updateToolbar = useCallback(() => {
		editor.read(() => {
			const selection = $getSelection();
			if ($isRangeSelection(selection)) {
				setIsBold(selection.hasFormat("bold"));
				setIsItalic(selection.hasFormat("italic"));
			}
		});
	}, [editor]);

	useEffect(() => {
		return editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			() => {
				updateToolbar();
				return false;
			},
			0
		);
	}, [editor, updateToolbar]);

	const handleFormat = useCallback(
		(format: "bold" | "italic") => {
			editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
		},
		[editor]
	);

	const handleListClick = useCallback(() => {
		handleList(editor);
	}, [editor]);

	const handleHeadingClick = useCallback(
		(headingLevel: "h2" | "h3") => {
			handleHeading(editor, headingLevel);
		},
		[editor]
	);

	const handleParagraphClick = useCallback(() => handleParagraph(editor), [editor]);

	const handleLinkClick = useCallback(() => {
		handleLink(editor);
	}, [editor]);

	const handleImageClick = useCallback(() => {
		handleImage(editor);
	}, [editor]);

	const handleClearFormattingClick = useCallback(() => {
		handleClearFormatting(editor);
	}, [editor]);

	return (
		<ToolbarWrapper>
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleFormat("bold");
				}}
				active={isBold}
				title="Bold (Ctrl+B)"
			>
				<strong>B</strong>
			</ToolbarButton>
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleFormat("italic");
				}}
				active={isItalic}
				title="Italic (Ctrl+I)"
			>
				<em>I</em>
			</ToolbarButton>
			<ToolbarDivider />
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleListClick();
				}}
				title="Bullet List"
			>
				• List
			</ToolbarButton>
			<ToolbarDivider />
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleHeadingClick("h2");
				}}
				title="Heading 2"
			>
				H2
			</ToolbarButton>
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleHeadingClick("h3");
				}}
				title="Heading 3"
			>
				H3
			</ToolbarButton>

			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleParagraphClick();
				}}
				title="Paragraph"
			>
				¶
			</ToolbarButton>
			<ToolbarDivider />
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleLinkClick();
				}}
				title="Link (Ctrl+K)"
			>
				🔗
			</ToolbarButton>
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleImageClick();
				}}
				title="Insert Image"
			>
				🖼️
			</ToolbarButton>
			<ToolbarDivider />
			<ToolbarButton
				type="button"
				onMouseDown={(e) => {
					e.preventDefault();
					handleClearFormattingClick();
				}}
				title="Clear Formatting"
			>
				⌫
			</ToolbarButton>
		</ToolbarWrapper>
	);
}

const ToolbarWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 8px 12px;
	border-bottom: 1px solid ${(props) => props.theme.colors.formWrapperBorder};
	background-color: #f9f9f9;
	border-radius: 4px 4px 0 0;
`;

const ToolbarButton = styled.button<{ active?: boolean }>`
	padding: 6px 10px;
	background-color: ${(props) => (props.active ? "#e0e0e0" : "white")};
	border: 1px solid #d0d0d0;
	border-radius: 3px;
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s;
	user-select: none;

	&:hover {
		background-color: #f0f0f0;
	}

	&:active {
		background-color: #e0e0e0;
	}

	strong {
		font-weight: bold;
	}

	em {
		font-style: italic;
	}
`;

const ToolbarDivider = styled.div`
	width: 1px;
	height: 24px;
	background-color: #d0d0d0;
	margin: 0 4px;
`;
